import { useMutation } from '@tanstack/react-query'
import { type FC, useCallback } from 'react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog'
import { buttonVariants } from '@/components/button/variant'

import { deleteApplicantsMutation } from '@/lib/queries/business/v1/applicant/mutations'

export interface Props {
  open: boolean
  applicantPathIds: number[]
  onDeleted: () => Promise<void>
  onClose: () => void
}

export const DeleteApplicantsDialog: FC<Props> = ({ open, applicantPathIds, onDeleted, onClose }) => {
  const mutationDelete = useMutation(deleteApplicantsMutation)

  const handleDelete = useCallback(async () => {
    try {
      await mutationDelete.mutateAsync({ pathIds: applicantPathIds })
      await onDeleted()
      onClose()
      toast.success('Applicants deleted successfully.')
    } catch (err) {
      await onDeleted()
      onClose()
      toast.error('Failed to delete applicants.')
    }
  }, [applicantPathIds, mutationDelete, onClose, onDeleted])

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Deleting {applicantPathIds.length} {applicantPathIds.length === 1 ? 'applicant' : 'applicants'}
          </AlertDialogTitle>

          <AlertDialogDescription>
            Are you sure you want to delete {applicantPathIds.length === 1 ? 'this applicant' : 'these applicants'}? All
            associated cases will also be permanently deleted.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Cancel</AlertDialogCancel>

          <AlertDialogAction
            className={buttonVariants({ variant: 'destructive' })}
            disabled={mutationDelete.isPending}
            onClick={handleDelete}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
